import { EventBus } from '@/shared/utils';
import { CatalogEvents, CatalogEventKeys } from '../events';
import { CourseDataFilters } from '../types/filtered-data';
import { CategoryTypes } from '../types/catalog-category-data';
import {
  buildLearningPathsFilter,
  buildMasterclassesFilter,
  buildSeriesFilter,
} from './filter-builder';

/**
 * Flexible API utility that handles both authenticated and non-authenticated calls
 */
export const catalogApiCall = async ({
  endpoint,
  method = 'GET',
  data = null,
  requiresAuth = false,
  token = null,
  params = null,
  eventBus = null,
  errorEventKey = null,
}: {
  endpoint: string;
  method?: string;
  data?: any;
  requiresAuth?: boolean;
  token?: string | null;
  params?: Record<string, any> | null;
  eventBus?: EventBus<CatalogEvents> | null;
  errorEventKey?: any;
}) => {
  try {
    // Construct URL with query parameters if provided
    let url = `${process.env.NEXT_PUBLIC_HOST}/api/catalog/${endpoint}`;
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url = `${url}?${queryString}`;
      }
    }

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add auth token if required
    if (requiresAuth) {
      if (!token) {
        const error = new Error('Authentication required but token not provided');
        if (eventBus && errorEventKey) {
          eventBus.emit(errorEventKey, error);
        }
        return { success: false, error };
      }
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Make the request
    const response = await fetch(url, {
      method,
      headers,
      body: data && method !== 'GET' ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      let errorMessage = `API call to ${endpoint} failed: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (parseError) {}

      const error = new Error(errorMessage);
      if (eventBus && errorEventKey) {
        eventBus.emit(errorEventKey, error instanceof Error ? error : new Error(String(error)));
      }
      return { success: false, error };
    }

    const responseData = await response.json();
    return { success: true, data: responseData };
  } catch (error: any) {
    console.error(`Error in API call to ${endpoint}:`, error);
    if (eventBus && errorEventKey) {
      eventBus.emit(errorEventKey, error instanceof Error ? error : new Error(String(error)));
    }
    return { success: false, error };
  }
};

// Non-authenticated API services (for SSG and guest users)
export const catalogServices = {
  // Get catalog view data (used for SSG and guest users)
  getCatalogView: async () => {
    return catalogApiCall({
      endpoint: 'view',
      method: 'GET',
    });
  },

  // Get category data
  getCategoryData: async (categoryName: CategoryTypes) => {
    return catalogApiCall({
      endpoint: 'category',
      method: 'GET',
      params: { categoryName },
    });
  },

  // Search functionality
  search: async (query: string, courseTypes?: string[]) => {
    return catalogApiCall({
      endpoint: 'search',
      method: 'POST',
      data: {
        query,
        courseType: courseTypes || ['Masterclass', 'Serie', 'Percorsi'],
      },
    });
  },

  // Get filtered data
  getFilteredData: async (
    filters: Partial<CourseDataFilters>,
    page: { page: number; pageSize: number },
  ) => {
    return catalogApiCall({
      endpoint: 'filtered',
      method: 'POST',
      data: {
        page,
        seriesFilters: buildSeriesFilter(filters),
        learningPathFilter: buildLearningPathsFilter(filters),
        masterclassFilter: buildMasterclassesFilter(filters),
        courseType: filters.types || [],
      },
    });
  },

  // Get search filtered data
  getSearchFilteredData: async (
    filters: Partial<CourseDataFilters>,
    page: { page: number; pageSize: number },
    query: string,
  ) => {
    return catalogApiCall({
      endpoint: 'search',
      method: 'POST',
      data: {
        page,
        seriesFilters: buildSeriesFilter(filters),
        learningPathFilter: buildLearningPathsFilter(filters),
        masterclassFilter: buildMasterclassesFilter(filters),
        courseType: filters.types || [],
        query,
      },
    });
  },

  // Get category filtered data
  getCategoryFilteredData: async (
    filters: Partial<CourseDataFilters>,
    page: { page: number; pageSize: number },
    categoryName: CategoryTypes,
  ) => {
    return catalogApiCall({
      endpoint: 'category',
      method: 'POST',
      data: {
        page,
        seriesFilters: buildSeriesFilter(filters),
        learningPathFilter: buildLearningPathsFilter(filters),
        masterclassFilter: buildMasterclassesFilter(filters),
        courseType: filters.types || [],
        category: categoryName,
      },
    });
  },
};

// User-aware API services (requires user token)
// These now use the same endpoints as catalogServices but include authentication
export const userCatalogServices = {
  // Get user-specific catalog view data
  getUserCatalogView: async (email: string, token: string, eventBus?: EventBus<CatalogEvents>) => {
    return catalogApiCall({
      endpoint: 'view', // Changed from 'user-view' to 'view'
      method: 'POST',
      data: { email },
      requiresAuth: true,
      token,
      eventBus,
      errorEventKey: CatalogEventKeys.catalog.error,
    });
  },

  // Get user-specific category data
  getUserCategoryData: async (
    email: string,
    categoryName: CategoryTypes,
    token: string,
    eventBus?: EventBus<CatalogEvents>,
  ) => {
    return catalogApiCall({
      endpoint: 'category', // Changed from 'user-category' to 'category'
      method: 'POST',
      data: { email, categoryName },
      requiresAuth: true,
      token,
      eventBus,
      errorEventKey: CatalogEventKeys.category.error,
    });
  },

  // User-specific search
  userSearch: async (
    email: string,
    query: string,
    courseTypes: string[] | undefined,
    token: string,
    eventBus?: EventBus<CatalogEvents>,
  ) => {
    return catalogApiCall({
      endpoint: 'search', // Changed from 'user-search' to 'search'
      method: 'POST',
      data: {
        email,
        query,
        courseType: courseTypes || ['Masterclass', 'Serie', 'Percorsi'],
      },
      requiresAuth: true,
      token,
      eventBus,
      errorEventKey: CatalogEventKeys.search.error,
    });
  },

  // User-specific filtered data
  getUserFilteredData: async (
    email: string,
    filters: Partial<CourseDataFilters>,
    page: { page: number; pageSize: number },
    token: string,
    eventBus?: EventBus<CatalogEvents>,
  ) => {
    return catalogApiCall({
      endpoint: 'filtered', // Changed from 'user-filtered' to 'filtered'
      method: 'POST',
      data: {
        email,
        page,
        seriesFilters: buildSeriesFilter(filters),
        learningPathFilter: buildLearningPathsFilter(filters),
        masterclassFilter: buildMasterclassesFilter(filters),
        courseType: filters.types || [],
      },
      requiresAuth: true,
      token,
      eventBus,
      errorEventKey: CatalogEventKeys.filter.error,
    });
  },

  // Favorites management
  favorites: {
    create: async (
      data: any,
      token: string,
      eventBus?: EventBus<CatalogEvents>,
      documentType?: 'serie' | 'masterclass' | 'learningPath',
    ) => {
      return catalogApiCall({
        endpoint: 'favourites/create',
        method: 'POST',
        data,
        requiresAuth: true,
        token,
        eventBus,
        errorEventKey: documentType
          ? CatalogEventKeys.favourite.create[documentType].error
          : undefined,
      });
    },

    delete: async (
      favouriteId: string,
      token: string,
      eventBus?: EventBus<CatalogEvents>,
      documentType?: 'serie' | 'masterclass' | 'learningPath',
    ) => {
      return catalogApiCall({
        endpoint: 'favourites/delete',
        method: 'DELETE',
        params: { favouriteId },
        requiresAuth: true,
        token,
        eventBus,
        errorEventKey: documentType
          ? CatalogEventKeys.favourite.delete[documentType].error
          : undefined,
      });
    },
  },

  // Get subscription status
  getSubscriptionStatus: async (
    email: string,
    token: string,
    eventBus?: EventBus<CatalogEvents>,
  ) => {
    return catalogApiCall({
      endpoint: 'subscription',
      method: 'GET',
      params: { email },
      requiresAuth: true,
      token,
    });
  },
};
